import { useEffect, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import {
  prop,
  values as rValues,
  length,
  pipe,
  find,
  isEmpty,
  isNil,
  complement,
  reject,
  map,
  flatten
} from 'ramda'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { select } from '@/store'
import { breakpoints, colors } from '@/styles'
import { Label } from '@/components/text'
import PencilIcon from './pencil-icon.svg'
import AddNewSignerIcon from './add-new-signer-icon.svg'
import ContainerInner from './container-inner'
import { show as showSelectRecipientModal } from './add-recipient-modal'
import { show as showSelectRecipientFormModal } from './add-recipient-form-modal'
import { getIsCreator } from '../utils'
import Context from '../context'

const MAX_SIGNEES = 7

const Container = styled.div`
  max-width: 1192px;
  margin: 34px 30px 80px;
`

const HeadingContainer = styled.div`
  display: flex;
  margin-bottom: 40px;
`

const Heading = styled.h5`
  margin-left: 10px;
  font-size: 20px;
  font-weight: bold;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    margin-left: 0;
  }
`

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 50px 3fr;
  width: 100%;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    display: block;
  }
`

const GridOne = styled.div`
  grid-column-start: 1;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    display: none;
  }
`

const GridTwo = styled.div`
  grid-column-start: 2;
  max-width: 760px;
`

const TitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`

const SenderReceiverTitle = styled.p`
  font-size: 16px;
  font-weight: 500;
  margin: 8px 0 0 0;
`

const RequiredAsterixHeader = styled(SenderReceiverTitle)`
  color: ${colors.rainbowRed};
  margin-left: 4px;
`

const DropdownContainer = styled.div`
  margin: 16px 0px 24px;
  width: 100%;
  border-radius: 8px;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06);
`

const AddNewSignerContainer = styled.button`
  background: none;
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 16px 0px 24px;
  width: 100%;
  height: 56px;
  border: 1.5px dashed ${colors.grey80};
  border-radius: 8px;
  cursor: pointer;

  &:hover,
  &:active {
    background-color: ${colors.blue96};
    border: 1.5px dashed ${colors.blue80};
  }
`

const SenderContainer = styled(DropdownContainer)`
  background: white;
`

const AddNewSignerIconStyle = styled.img`
  width: 32px;
  height: 32px;
  margin-right: 8px;
`

const AddNewSignerContainerText = styled(Label)`
  color: ${colors.blue16};
`

const RequiredAsterixFooter = styled(AddNewSignerContainerText)`
  margin-left: 4px;
  color: ${colors.rainbowRed};
`

const AddSigner = ({ handleValidate, contractId, signees, setSignees, items = {} }) => {
  const { recipients = {}, applicants } = useSelector(state =>
    select.contract.contractPackageInfo(state, { contractId })
  )

  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { fileMetadata, setFileMetadata, user: currentUser, setUser } = useContext(Context)
  const signingLandlord = pipe(rValues, find(getIsCreator))(recipients) ?? {}
  const signingTenants = pipe(rValues, reject(getIsCreator))(recipients) ?? []
  const isSigningTenantsEmpty = isEmpty(signingTenants)

  const hasApplicants = pipe(
    rValues,
    map(prop('applicants')),
    flatten,
    complement(isEmpty)
  )(applicants)

  useEffect(() => {
    async function fetchData() {
      await dispatch.contract.getRecipients({ contractId: contractId })
    }

    if (isEmpty(recipients)) fetchData()
  }, [])

  useEffect(() => {
    async function fetchData() {
      await dispatch.contract.getContractApplicants({ contractId: contractId })
    }

    if (isNil(applicants)) fetchData()
  }, [])

  useEffect(() => {
    isSigningTenantsEmpty ? handleValidate(false) : handleValidate(true)
  }, [isSigningTenantsEmpty])

  const handleAddRecipient = () =>
    !hasApplicants
      ? showSelectRecipientFormModal({
          contractId,
          signees,
          setSignees,
          fileMetadata,
          setFileMetadata,
          currentUser,
          setUser
        })
      : showSelectRecipientModal({
          contractId,
          signees,
          setSignees,
          fileMetadata,
          setFileMetadata,
          currentUser,
          setUser
        })

  return (
    <Container>
      <HeadingContainer>
        <Heading>
          {t('l.contract_edit.add_recipients.add_recipients.title', 'Add Recipients')}
        </Heading>
      </HeadingContainer>
      <GridContainer>
        <GridOne>
          <img src={PencilIcon} alt="title" />
        </GridOne>
        <GridTwo>
          <div>
            <TitleContainer>
              <SenderReceiverTitle>
                {t('l.contract_edit.add_recipients.recipients.title', 'Recipients')}
              </SenderReceiverTitle>
              <RequiredAsterixHeader>*</RequiredAsterixHeader>
            </TitleContainer>
            <SenderContainer>
              <ContainerInner
                user={signingLandlord}
                role="landlord"
                showEdit={false}
                contractId={contractId}
                signees={signees}
                setSignees={setSignees}
                items={items}
              />
            </SenderContainer>
          </div>
          <div>
            {!!length(signingTenants) &&
              signingTenants.map((tenant, i) => {
                return (
                  <SenderContainer key={`existing-signees-${i}`}>
                    <ContainerInner
                      user={tenant}
                      role="renter"
                      showEdit={true}
                      allowEdit={!tenant.is_applicant}
                      contractId={contractId}
                      signees={signees}
                      setSignees={setSignees}
                      items={items}
                    />
                  </SenderContainer>
                )
              })}

            {length(rValues(recipients)) < MAX_SIGNEES && (
              <AddNewSignerContainer onClick={handleAddRecipient} type="button">
                <AddNewSignerIconStyle src={AddNewSignerIcon} alt="" />
                <AddNewSignerContainerText fontSize="14px">
                  {t('l.contract_edit.add_recipient.recipients.button', 'Add Recipient')}
                </AddNewSignerContainerText>
                {isEmpty(signingTenants) && (
                  <RequiredAsterixFooter fontSize="14px">*</RequiredAsterixFooter>
                )}
              </AddNewSignerContainer>
            )}
          </div>
        </GridTwo>
      </GridContainer>
    </Container>
  )
}

export default AddSigner
